/**
 * Photo crop geometry — the TWO crops every surface cuts from the ONE stored
 * face box (SYSTEM §8 "At launch — the photo's crops", Drop 3, 1 Oct 2026;
 * README request 3). Crops are calculated, never stored.
 *
 * 1. The round face (Intro Card, Detail, Profile, every small round avatar):
 *    the face box is ROUND_FACE_SHARE of the circle's diameter, centred, its
 *    middle ROUND_FACE_DOWN of the way down; the photo always covers the
 *    circle — too tight for the framing → zoom in and slide, never a gap.
 * 2. The People square (People panel + the photo QR's source + the person's
 *    colour): face centred side to side, its middle 40% down, its height ¼ of
 *    the side; the square shrinks to fit the photo, never a gap; no face →
 *    the top-middle crop.
 *
 * Pure — ports to native as-is.
 */

// The round face was first settled as the HEAD filling 72% of the circle
// (27 Sep 2026, packet answer 2). The 72% was approved BY SIGHT on the designer's hand-measured boxes, which
// span the whole head (hair included). The detector's box is the face alone.
// Measured on the drop's photos where both exist (plan Step 4): head ÷ box =
// Ella 1.76, Léa 2.0, Marcus 1.47, Emily 1.77, Jake 1.78 → 1.75. The stored
// box stays the detector's raw truth; the calibration lives only here.
export const HEAD_PER_FACE_BOX = 1.75;

// SETTLED 1 Oct 2026 (SYSTEM §8): the detector's face box fills 41% of the
// circle — CD's own reading of that 72% head fill (0.72 / 1.75 ≈ 0.41,
// detail-engine.jsx), stated as the number every engine now draws with.
export const ROUND_FACE_SHARE = 0.41;
// …with the face's middle 48% of the way down the circle (was centred).
export const ROUND_FACE_DOWN = 0.48;

/**
 * The round face: where the photograph sits inside a D-px circle.
 *
 * @param {{face: {x,y,w,h}|null, aspect: number|null}} meta - readPhotoMeta() output
 * @param {number} D - circle diameter in px
 * @returns {{left:number, top:number, width:number, height:number}|null} the
 *   image rect relative to the circle, or null when the aspect is unknown
 *   (legacy photo — render as a centred background `cover` instead)
 */
export const roundAvatarRect = (meta, D) => {
  const aspect = meta?.aspect;
  if (!(aspect > 0) || !(D > 0)) return null;

  const face = meta.face;
  const cx = face ? face.x + face.w / 2 : 0.5;
  const cy = face ? face.y + face.h / 2 : 0.5;

  // Scale so the face box is its share of the circle (no face → just cover),
  // never below cover: the picture must span the circle both ways.
  const faceScaled = face ? (D * ROUND_FACE_SHARE) / face.h : D;
  const H = Math.max(faceScaled, D, D / aspect);
  const W = H * aspect;

  const left = Math.min(0, Math.max(D - W, D / 2 - cx * W));
  const top = Math.min(0, Math.max(D - H, D * (face ? ROUND_FACE_DOWN : 0.5) - cy * H));
  return { left, top, width: W, height: H };
};

// The People square (PEOPLE W10): the face's height is ¼ of the side, its
// middle 40% down. No face → the largest square at object-position 50% 20%
// (the reference's "today's crop", people-engine.jsx PeFaceSq).
export const SQUARE_FACE_SHARE = 0.25;
export const SQUARE_FACE_DOWN = 0.4;
export const SQUARE_NO_FACE_DOWN = 0.2;

/**
 * The People square, as a rect of the source photo in 0–1 fractions. The same
 * rect feeds the People panel and the photo QR.
 *
 * @param {{face: {x,y,w,h}|null, aspect: number|null}} meta - readPhotoMeta() output
 * @returns {{x:number, y:number, w:number, h:number}|null} null when the aspect
 *   is unknown (no stored meta — the caller keeps its own default)
 */
export const peopleSquareRect = (meta) => {
  const aspect = meta?.aspect;
  if (!(aspect > 0)) return null;
  // Work in source pixels with the photo 1 tall: W = aspect, H = 1.
  const W = aspect;
  const face = meta.face;
  if (!face) {
    const S = Math.min(W, 1);
    return { x: (W - S) / 2 / W, y: (1 - S) * SQUARE_NO_FACE_DOWN, w: S / W, h: S };
  }
  const S = Math.min(face.h / SQUARE_FACE_SHARE, W, 1);
  const cx = (face.x + face.w / 2) * W;
  const cy = face.y + face.h / 2;
  const L = Math.max(0, Math.min(W - S, cx - S / 2));
  const T = Math.max(0, Math.min(1 - S, cy - SQUARE_FACE_DOWN * S));
  return { x: L / W, y: T, w: S / W, h: S };
};
